import type { ApiClient } from '@carbonwebmail/api-client'
import type { DuplicateGroupDto } from '@carbonwebmail/contracts'
import { Alert } from '../../ui/Alert.js'
import { Async } from '../../ui/Async.js'
import { Checkbox } from '../../ui/Checkbox.js'
import { Dialog } from '../../ui/Dialog.js'
import { count } from '../../ui/format.js'
import { useDuplicates } from './useDuplicates.js'

/**
 * Finding the contacts that reach one mailbox, and clearing them.
 *
 * A dialog rather than a tab. Looking for duplicates is something somebody does
 * to the board occasionally and then leaves; a permanent tab spent the width of
 * the strip on it and put it beside the views, which are what the board is for.
 *
 * **Only addresses that reach one inbox are offered.** A plus tag, or a dot at
 * a provider that ignores them. Not similar names: "J Smith" and "John Smith"
 * at different addresses are two people, and a finder that guesses is one whose
 * suggestions nobody dares accept. The mailbox is shown, so each group explains
 * itself.
 *
 * **The oldest is kept and it gains what the others held.** That was the
 * default on the screen this replaces, accepted group by group; choosing it
 * once is the whole point of a button that clears a list. The rows that go are
 * deleted — migration 0028 is what makes that safe, by keeping the record of
 * what was sent to them.
 *
 * Nobody is unsuppressed and nobody is resubscribed: the suppression list is
 * keyed on the address and outlives every contact row, so clearing duplicates
 * can never become a way to start mailing somebody who asked you to stop.
 */
export function DuplicatesDialog({
  client,
  onCleared,
  onClose,
}: {
  readonly client: ApiClient
  /** Told when rows actually went, so the board behind reloads. */
  readonly onCleared: () => void
  readonly onClose: () => void
}) {
  const duplicates = useDuplicates({ client, onCleared })
  const { state, picked, notice, problem, busy } = duplicates

  const found = state.status === 'ready' ? state.value.length : 0

  return (
    <Dialog heading="Duplicates" onClose={onClose}>
      <p className="mb-4 text-[13px] text-t2">
        Addresses that reach the same inbox — a plus tag, or a dot where the provider ignores one.
        Clearing keeps the contact added first and moves anything the others held onto it.
      </p>

      {problem === undefined ? null : <Alert>{problem}</Alert>}
      {notice === undefined ? null : <Alert tone="ok">{notice}</Alert>}

      <Async
        state={state}
        skeleton={<p className="text-[13px] text-t3">Looking…</p>}
        onRetry={() => void duplicates.retry()}
      >
        {(groups) =>
          groups.length === 0 ? (
            <p className="py-8 text-center text-[13px] text-t2">
              No two contacts share a mailbox. This looks for addresses that differ only in ways no
              mail provider honours, so it will not find two people with similar names.
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {groups.map((group) => (
                <Group
                  busy={busy}
                  group={group}
                  key={group.mailbox}
                  onToggle={duplicates.toggle}
                  ticked={picked.includes(group.mailbox)}
                />
              ))}
            </ul>
          )
        }
      </Async>

      <div className="mt-5 flex flex-wrap items-center justify-end gap-2 border-line border-t pt-4">
        <button
          className="mr-auto rounded-pill px-4 py-2 font-semibold text-[13px] text-t2 hover:text-t1"
          onClick={onClose}
          type="button"
        >
          Cancel
        </button>

        <button
          className="rounded-pill border border-line px-4 py-2 font-semibold text-[13px] text-t1 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={busy || picked.length === 0}
          onClick={() => void duplicates.clear(picked)}
          type="button"
        >
          Clear selected ({count(picked.length)})
        </button>

        {/*
          Undefined rather than every mailbox on screen: "all" means whatever is
          there when the server reads it, which is the honest meaning of the
          word and the only one that cannot act on a stale list.
        */}
        <button
          className="rounded-pill bg-brand px-5 py-2 font-bold text-[13px] text-brand-contrast disabled:cursor-not-allowed disabled:opacity-50"
          disabled={busy || found === 0}
          onClick={() => void duplicates.clear(undefined)}
          type="button"
        >
          Clear all ({count(found)})
        </button>
      </div>
    </Dialog>
  )
}

function Group({
  group,
  ticked,
  busy,
  onToggle,
}: {
  readonly group: DuplicateGroupDto
  readonly ticked: boolean
  readonly busy: boolean
  readonly onToggle: (mailbox: string) => void
}) {
  const [keep, ...going] = group.contacts

  return (
    <li className="flex items-start gap-3 rounded-field border border-line bg-surface-2 p-3">
      <span className="pt-0.5">
        <Checkbox
          checked={ticked}
          disabled={busy}
          label={`Select ${group.mailbox}`}
          onChange={() => onToggle(group.mailbox)}
        />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-semibold text-[14px] text-t1">{group.mailbox}</span>
        {/* Which row survives is shown rather than implied: this deletes the
            others, and somebody should see which before pressing. */}
        <span className="mt-1 block text-[12px] text-t2">
          Keeping <span className="font-medium text-t1">{keep?.email}</span>
          {', removing '}
          {going.map((contact) => contact.email).join(', ')}
        </span>
      </span>
    </li>
  )
}
